import { collector } from "../collector";
import { docScanFailCount } from "../failureOutcome";
import { sanitizeUrl } from "../redact";

// Wrap window.fetch to record every request (URL sanitized to host+path) and to
// derive the two Contact-support counters from server-authoritative response
// fields — so the 5 document modules need no edits:
//   - server_timeout: app-level timeout signals (status:"timeout" /
//     overallStatus:"_TIMEOUT" / isTimeout) or an aborted request.
//   - doc_scan_failed: the backend's attempt counter, taken only from responses
//     that report a failed attempt (see ../failureOutcome) — that counter
//     tallies every finalized attempt, successes included.

function isEkyc(url) {
  return (
    typeof url === "string" &&
    url.indexOf("/ekyc") !== -1 &&
    url.indexOf("/ekyc/diagnostics") === -1
  );
}

function looksLikeTimeout(body) {
  if (!body || typeof body !== "object") return false;
  if (body.status === "timeout" || body.isTimeout === true || body.overallStatus === "_TIMEOUT") return true;
  const d = body.data;
  if (d && typeof d === "object") {
    if (d.isTimeout === true || d.overallStatus === "_TIMEOUT" || d.status === "timeout") return true;
  }
  return false;
}

async function inspect(url, res) {
  if (!isEkyc(url)) return;
  let body;
  try {
    body = await res.clone().json();
  } catch (e) {
    return; // non-JSON or already consumed
  }
  if (!body || typeof body !== "object") return;
  if (looksLikeTimeout(body)) collector.recordServerTimeout({ url: sanitizeUrl(url) });
  const failCount = docScanFailCount(body);
  if (failCount != null) collector.syncDocScanFailCount(failCount, { url: sanitizeUrl(url) });
}

export function startNetwork() {
  const raw = window.fetch;
  if (!raw || raw.__diagWrapped) return () => {};

  const now = () => (window.performance && performance.now ? performance.now() : Date.now());

  const wrapped = function (input, init) {
    let url = "";
    try {
      url = typeof input === "string" ? input : (input && input.url) || "";
    } catch (e) {
      url = "";
    }
    // Never instrument diagnostics/ping traffic (recursion / noise).
    if (typeof url === "string" && url.indexOf("/ekyc/diagnostics") !== -1) {
      return raw.apply(this, arguments);
    }
    const method =
      (init && init.method) || (typeof input === "object" && input && input.method) || "GET";
    const t0 = now();

    return raw.apply(this, arguments).then(
      (res) => {
        // Tracking must never affect the real request result.
        try {
          collector.track("network_request", {
            url: sanitizeUrl(url),
            method,
            status: res.status,
            ok: res.ok,
            durationMs: Math.round(now() - t0),
          });
          inspect(url, res).catch(() => {});
        } catch (e) {
          /* swallow */
        }
        return res;
      },
      (err) => {
        // Never mask the real error — track defensively, then rethrow.
        try {
          const aborted = err && err.name === "AbortError";
          collector.track("network_error", {
            url: sanitizeUrl(url),
            method,
            kind: aborted ? "timeout" : "network",
            durationMs: Math.round(now() - t0),
            message: err && err.message,
          });
          // Only an actual abort/timeout counts toward the server-timeout trigger;
          // generic offline blips are captured by the connectivity collector.
          if (aborted && isEkyc(url)) collector.recordServerTimeout({ url: sanitizeUrl(url), reason: "abort" });
        } catch (e) {
          /* swallow */
        }
        throw err;
      }
    );
  };
  wrapped.__diagWrapped = true;
  window.fetch = wrapped;

  return () => {
    if (window.fetch === wrapped) window.fetch = raw;
  };
}
